<template>
	<AppCardPage class="finance-refined ll-refined">
		<div class="finance-refined-forms">
			<AppCard title="Situatie" card-id="situatie" :collapsible="true">
				<SchemaForm :schema="state.situatieSchema" class="page-form" />
			</AppCard>
			<AppCard title="Kosten" card-id="kosten" :collapsible="true">
				<template #subtitle>
					<AppCardListSubtitle :subtitle="formValues?.totaalNodig" />
				</template>
				<SchemaForm :schema="state.kostenSchema" class="page-form" />

				<hr />
				<RowText
					:read-only="true"
					label="Totale kosten"
					:model-value="formValues?.totaalNodig"
					:value-type="ValueType.Currency"
					class="font-semibold"
				/>
			</AppCard>
			<AppCard title="Hypotheek" card-id="leningdelen" :collapsible="true">
				<template #subtitle>
					<AppCardListSubtitle :subtitle="shownMortgagePartsTotal" />
				</template>
				<SchemaForm
					:schema="state.leningdelenSchema"
					:prevent-model-cleanup-on-schema-change="true"
					class="page-form"
				/>

				<hr />
				<RowText
					:read-only="true"
					label="Totale hoogte"
					:model-value="shownMortgagePartsTotal"
					:value-type="ValueType.Currency"
					class="font-semibold"
				/>
				<RouterLink
					:to="{ name: 'Hypotheek' }"
					class="finance-refined-edit-link"
					>Leningdelen bewerken</RouterLink
				>
			</AppCard>
			<AppCard title="Middelen" card-id="middelen" :collapsible="true">
				<template #subtitle>
					<AppCardListSubtitle :subtitle="formValues?.middelenResultaat" />
				</template>
				<div class="page-form">
					<RowOption
						label="Eigen middelen bepalen"
						:options="{
							auto: 'Automatisch berekenen',
							manual: 'Handmatig invullen',
						}"
						:model-value="formValues?.eigenMiddelenInputType"
						@update:model-value="
							(v: string | number | boolean | undefined) => {
								if (formValues)
									formValues.eigenMiddelenInputType = v as 'auto' | 'manual';
							}
						"
					/>
				</div>
				<SchemaForm
					:schema="state.middelenSchema"
					:prevent-model-cleanup-on-schema-change="true"
					class="page-form"
				/>

				<hr />
				<RowText
					:read-only="true"
					label="Totale middelen"
					:model-value="formValues?.middelenResultaat"
					:value-type="ValueType.Currency"
					class="font-semibold"
				/>
			</AppCard>
			<AppCard title="Resultaat" card-id="resultaat" :collapsible="true">
				<template #subtitle>
					<AppCardListSubtitle
						:class="formValues?.resultInfo?.className"
						:subtitle="formValues?.financiering?.financieringsresultaat"
					/>
				</template>
				<SchemaForm :schema="state.resultaatSchema" class="page-form" />
			</AppCard>
			<AppCard
				v-if="showValues"
				title="Dev - modelvalues"
				card-id="opzetModel"
				:collapsible="true"
			>
				<pre>{{ stringifyObject(formValues) }}</pre>
			</AppCard>
		</div>
		<aside
			class="finance-refined-summary"
			aria-label="Samenvatting financieringsopzet"
		>
			<p class="finance-refined-overline">Financieringsopzet</p>
			<h2>Totaal benodigd</h2>
			<output class="finance-refined-amount">{{
				currency(formValues?.totaalNodig)
			}}</output>
			<dl>
				<div>
					<dt>Benodigde financiering</dt>
					<dd>{{ currency(formValues?.benodigdeFinancieringResultaat) }}</dd>
				</div>
				<div>
					<dt>Hypotheek · alle delen</dt>
					<dd>{{ currency(mortgagePartsTotal.hoogte) }}</dd>
				</div>
				<div>
					<dt>
						Eigen middelen ·
						{{
							formValues?.eigenMiddelenInputType === "auto"
								? "benodigd"
								: "opgegeven"
						}}
					</dt>
					<dd>
						{{ currency(formValues?.benodigdeFinanciering?.eigenMiddelen) }}
					</dd>
				</div>
				<div>
					<dt>Totale middelen</dt>
					<dd>{{ currency(formValues?.middelenResultaat) }}</dd>
				</div>
				<div class="finance-refined-balance">
					<dt>{{ formValues?.resultInfo?.label }}</dt>
					<dd>
						{{ currency(formValues?.financiering?.financieringsresultaat) }}
					</dd>
				</div>
			</dl>
			<p>
				Rekenkundig overzicht. Beschikbaarheid en acceptatie zijn niet getoetst.
			</p>
		</aside>
	</AppCardPage>
</template>

<script setup lang="ts">
import { inject, toRef } from "vue";
import useFinancieringsOpzet from "@/store/financiering/tabs/useFinancieringsOpzet";
import { SchemaForm, useSchemaForm } from "formvuelate";
import { stringifyObject } from "@/helpers/uiHelpers";
import { ValueType } from "@/enums/ValueType";
import { storeToRefs } from "pinia";
import RowOption from "@/components/input-formvuelate/RowOption.vue";

import useLeningdelen from "@/store/financiering/tabs/useLeningdelen";
import { formatNumericWhole } from "@/helpers/inputHelpers";
import "@/style/finance-refined.scss";

const { mortgagePartsTotal } = storeToRefs(useLeningdelen());
const currency = (value?: number) =>
	value === undefined ? "" : `€ ${formatNumericWhole(value)}`;

const financieringsOpzetStore = useFinancieringsOpzet();
const { state, shownMortgagePartsTotal } = storeToRefs(financieringsOpzetStore);

const formValues = toRef(() => state.value.values);
useSchemaForm(formValues);
const showValues = inject("DEBUG_SHOW_MODELVALUES", false);
</script>
